import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { Loader, Alert, Empty, formatDate } from '../../components/ui.jsx';

/**
 * The Contact inbox.
 *
 * Messages arrive from a public form, so this is a queue rather than a
 * log: the question is always "what still needs somebody", which is why
 * it opens on the unread ones and why the count at the top is of
 * everything rather than of whatever filter happens to be on.
 *
 * Each message is shown whole. A list of first lines would mean opening
 * every one to find out which matter, and these are short enough that
 * truncating them buys nothing.
 */

const TOPIC = {
  safeguarding: { icon: '🛟', label: "A child's safety", cls: 'pill-bad' },
  school: { icon: '🏫', label: 'School enquiry', cls: 'pill-good' },
  account: { icon: '🔑', label: 'Account trouble', cls: 'pill-warn' },
  payment: { icon: '💳', label: 'Payment', cls: 'pill-warn' },
  feedback: { icon: '💬', label: 'Feedback', cls: '' },
  other: { icon: '📨', label: 'Something else', cls: '' },
};

const STATUS = {
  new: { label: 'Unread', cls: 'pill-warn' },
  read: { label: 'Read', cls: '' },
  answered: { label: 'Answered', cls: 'pill-good' },
};

const FILTERS = [
  { id: '', label: 'Everything' },
  { id: 'new', label: 'Unread' },
  { id: 'read', label: 'Read' },
  { id: 'answered', label: 'Answered' },
];

export default function SuperMessages() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('new');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    (status) =>
      api
        .contactMessages(status ? { status } : {})
        .then(setData)
        .catch((err) => setError(err.message)),
    [],
  );

  useEffect(() => {
    setData(null);
    load(filter);
  }, [filter, load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.setMessageStatus(id, status);
      await load(filter);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (msg) => {
    // Deleting is how somebody's name and address leave this machine,
    // so it asks first and says whose.
    const ok = window.confirm(
      `Delete the message from ${msg.name} (${msg.email})?\n\nIt cannot be recovered.`,
    );
    if (!ok) return;
    setBusyId(msg.id);
    try {
      await api.deleteMessage(msg.id);
      await load(filter);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const counts = data?.counts;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>📨 Messages</h1>
          <p>
            Everything sent from the Contact page, newest first.
            {counts && counts.unread > 0 && (
              <>
                {' '}
                <strong>
                  {counts.unread} still unread
                </strong>{' '}
                of {counts.total}.
              </>
            )}
            {counts && counts.unread === 0 && counts.total > 0 && ' Nothing unread.'}
          </p>
        </div>
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="msg-filters" role="tablist" aria-label="Filter messages">
        {FILTERS.map((f) => (
          <button
            key={f.id || 'all'}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`lab-tab ${filter === f.id ? 'is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id === 'new' && counts?.unread > 0 && (
              <span className="pill pill-warn msg-filter-count">{counts.unread}</span>
            )}
          </button>
        ))}
      </div>

      {!data ? (
        <Loader label="Opening the inbox…" />
      ) : data.messages.length === 0 ? (
        <Empty
          icon="📭"
          title={filter === 'new' ? 'Nothing unread' : 'Nothing here'}
          text={
            filter === 'new'
              ? 'Every message has been looked at. New ones land here as they arrive.'
              : 'Messages sent from the Contact page appear here.'
          }
        />
      ) : (
        <div className="msg-list">
          {data.messages.map((m) => {
            const topic = TOPIC[m.topic] ?? TOPIC.other;
            const status = STATUS[m.status] ?? STATUS.read;
            const working = busyId === m.id;

            return (
              <article key={m.id} className={`msg card ${m.status === 'new' ? 'is-unread' : ''}`}>
                <header className="msg-head">
                  <div className="msg-who">
                    <strong>{m.name}</strong>
                    {/* A mail link, because answering is the whole point
                        and retyping an address is how it gets mistyped. */}
                    <a href={`mailto:${m.email}`} className="msg-email">{m.email}</a>
                  </div>
                  <div className="msg-tags">
                    <span className={`pill ${topic.cls}`}>{topic.icon} {topic.label}</span>
                    <span className={`pill ${status.cls}`}>{status.label}</span>
                    {m.senderType && (
                      <span className="pill" title="They were signed in when they sent it">
                        signed in · {m.senderType}
                      </span>
                    )}
                  </div>
                </header>

                {/* Whitespace preserved: people write in paragraphs, and
                    collapsing them turns a clear message into a wall. */}
                <p className="msg-body">{m.message}</p>

                <footer className="msg-foot">
                  <span className="small faint">
                    {formatDate(m.createdAt)}
                    {m.handledAt && ` · handled ${formatDate(m.handledAt)}`}
                  </span>
                  <div className="msg-actions">
                    {m.status !== 'read' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={working}
                        onClick={() => setStatus(m.id, 'read')}
                      >
                        Mark read
                      </button>
                    )}
                    {m.status !== 'answered' && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={working}
                        onClick={() => setStatus(m.id, 'answered')}
                      >
                        ✓ Answered
                      </button>
                    )}
                    {m.status !== 'new' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-quiet"
                        disabled={working}
                        onClick={() => setStatus(m.id, 'new')}
                      >
                        Back to unread
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-quiet msg-delete"
                      disabled={working}
                      onClick={() => remove(m)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}
